import { strings } from "@perceptron/constants";
import { AnnotationType } from "@perceptron/core/types";
import { Icon } from "@perceptron/ui/components/icon";
import { Tooltip, TooltipContent, TooltipTrigger } from "@perceptron/ui/components/tooltip";
import { cn } from "@perceptron/utils/cn";
import { isNumber, isString } from "lodash-es";

import { useStreamSource } from "@demo/contexts/StreamSourceContext";
import { useAnnotationInteractions } from "@demo/hooks/annotations/useAnnotationInteractions";
import {
  formatAnnotationLabel,
  formatLabelByCounter,
  getAnnotationKey,
  getAnnotationDomId,
} from "@demo/utils/annotations";

export interface ClipAnnotationTextProps {
  children?: React.ReactNode;
}

function ClipAnnotationText({ children, ...props }: ClipAnnotationTextProps & { [key: string]: unknown }) {
  const source = useStreamSource();
  const { t, mention, number } = props;
  const { handleClick } = useAnnotationInteractions();

  const time = isString(t) ? t : undefined;
  const annotationNumber = typeof number === "number" ? number : parseInt(String(number), 10);
  // Generate composite annotation key using source
  const annotationKey = !isNaN(annotationNumber) ? getAnnotationKey(annotationNumber, source) : "";
  const domId = !isNaN(annotationNumber) ? `clip-annotation-${getAnnotationDomId(annotationNumber, source)}` : "";
  const labelNumber = props["data-label-number"];
  const label = isString(mention)
    ? mention
    : isNumber(labelNumber)
      ? formatLabelByCounter(AnnotationType.CLIP, labelNumber)
      : formatAnnotationLabel(AnnotationType.CLIP, annotationKey);

  // Invalid clip fallback
  if (!time || isNaN(annotationNumber)) {
    const fallbackLabel = isString(mention) ? mention : "clip";
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              "relative inline-flex max-w-full cursor-pointer items-center gap-1 overflow-hidden whitespace-nowrap rounded-sm border px-1 py-0.5 label-2",
              "border-zinc-500/40 bg-zinc-500/15 text-zinc-400",
            )}
          >
            <Icon className="text-middle inline-block size-4 align-middle" name="alert-triangle" />
            {fallbackLabel}
          </span>
        </TooltipTrigger>
        <TooltipContent>{strings.annotations.invalid.clipMalformedTimestamp}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <span
      className={cn(
        "relative inline-block max-w-full animate-fade-in cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap align-bottom transition-colors duration-200",
        "rounded-sm border px-1",
        "border-blue bg-[#2B89E8]/40 transition-colors duration-200",
        `hover:bg-[#2B89E8]`,
        "py-0.5 label-2",
      )}
      data-open="false"
      id={domId}
      onClick={() => handleClick(annotationKey, source)}
      {...props}
    >
      <Icon className="text-middle mb-0.5 mr-1 inline-block size-4 align-middle" name="timer" />
      {label}
    </span>
  );
}

export default ClipAnnotationText;
